// File: frontend/src/views/private/Policies.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../services/apiService';
import { formatDate } from '../../utils/formatters';
import PageHeader from '../../components/layout/PageHeader';
import PageContainer from '../../components/layout/PageContainer';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import { 
  Wallet, Upload, AlertCircle, CheckCircle, Loader2,
  Building2, Calendar, Car, Home, Heart, Umbrella,
  Scale, Briefcase, Shield, Hash, ChevronRight, Plus,
  FileText, Clock
} from 'lucide-react';
import styles from './Policies.module.css';

const Policies = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFiles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getUserFiles({}, user.user_id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load files');
      }

      setFiles(response.data.files || []);
    } catch (err) {
      console.error('Load files error:', err);
      setError(err.message || 'Failed to load files');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewFile = (fileId) => {
    navigate(`/dashboard/policy-details/${fileId}`);
  };

  // ── Helpers ──
  const getPolicyTypeDisplay = (type) => {
    const map = {
      auto: 'Auto Insurance', auto_policy: 'Auto Insurance',
      home: 'Home Insurance', homeowners: 'Homeowners Insurance',
      renters: 'Renters Insurance', health: 'Health Insurance',
      life: 'Life Insurance', umbrella: 'Umbrella Policy',
      liability: 'Liability Insurance', commercial: 'Commercial Insurance',
      business: 'Business Insurance', condo: 'Condo Insurance',
      flood: 'Flood Insurance', fire: 'Fire Insurance',
    };
    return map[(type || '').toLowerCase()] || formatType(type) || 'Insurance Policy';
  };

  const formatType = (type) => {
    if (!type) return '';
    return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getPolicyTypeIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('auto') || t.includes('vehicle') || t.includes('car')) return Car;
    if (t.includes('home') || t.includes('property') || t.includes('dwelling') || t.includes('condo')) return Home;
    if (t.includes('health') || t.includes('medical')) return Heart;
    if (t.includes('life')) return Heart;
    if (t.includes('umbrella')) return Umbrella;
    if (t.includes('liability')) return Scale;
    if (t.includes('business') || t.includes('commercial')) return Briefcase;
    return Shield;
  };

  const getPolicyTypeColor = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('auto') || t.includes('vehicle') || t.includes('car')) return styles.typeAuto;
    if (t.includes('home') || t.includes('property') || t.includes('dwelling') || t.includes('condo')) return styles.typeHome;
    if (t.includes('health') || t.includes('medical')) return styles.typeHealth;
    if (t.includes('life')) return styles.typeLife;
    if (t.includes('umbrella')) return styles.typeUmbrella;
    if (t.includes('liability') || t.includes('business') || t.includes('commercial')) return styles.typeBusiness;
    return styles.typeDefault;
  };

  const getFileStatusConfig = (status) => {
    const configs = {
      completed: { label: 'Analyzed', icon: CheckCircle, className: styles.statusCompleted },
      processing: { label: 'Processing', icon: Loader2, className: styles.statusProcessing },
      failed: { label: 'Failed', icon: AlertCircle, className: styles.statusFailed },
      uploaded: { label: 'Uploaded', icon: Clock, className: styles.statusUploaded },
    };
    return configs[status] || configs.uploaded;
  };

  const getPolicyPeriodLabel = (summary) => {
    if (summary?.effective_date && summary?.expiry_date) {
      return `${formatDate(summary.effective_date, 'long')} → ${formatDate(summary.expiry_date, 'long')}`;
    }
    if (summary?.effective_date) {
      const term = summary.policy_term_months;
      return `From ${formatDate(summary.effective_date, 'long')}${term ? ` · ${term} months` : ''}`;
    }
    return null;
  };

  // Separate completed policies from processing/failed
  const completedPolicies = files.filter(f => f.file_status === 'completed' && f.metadata_summary);
  const pendingFiles = files.filter(f => f.file_status !== 'completed' || !f.metadata_summary);

  // Group completed policies by type
  const policyGroups = {};
  completedPolicies.forEach(file => {
    const type = file.metadata_summary?.policy_type || 'other';
    if (!policyGroups[type]) policyGroups[type] = [];
    policyGroups[type].push(file);
  });

  if (isLoading) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader 
          title="My Insurance Wallet"
          subtitle="Your digital insurance portfolio"
          icon={<Wallet size={24} strokeWidth={2} />}
          showBack={true}
          backTo="/dashboard"
        />
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h2>Failed to Load Policies</h2>
            <p>{error}</p>
            <button onClick={loadFiles} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader 
        title="My Insurance Wallet"
        subtitle={files.length > 0 
          ? `${completedPolicies.length} ${completedPolicies.length === 1 ? 'policy' : 'policies'} in your wallet`
          : 'Your digital insurance portfolio'
        }
        icon={<Wallet size={24} strokeWidth={2} />}
        showBack={true}
        backTo="/dashboard"
        actions={
          <button 
            onClick={() => navigate('/dashboard/upload')} 
            className={styles.uploadButton}
          >
            <Plus size={18} strokeWidth={2.5} />
            <span>Add Policy</span>
          </button>
        }
      />

      {files.length === 0 ? (
        <div className={styles.emptyContainer}>
          <div className={styles.emptyCard}>
            <div className={styles.emptyIconWrap}>
              <Wallet size={48} strokeWidth={1.5} />
            </div>
            <h2>Your wallet is empty</h2>
            <p>Upload your insurance policies and we'll organize them into your digital wallet automatically</p>
            <button 
              onClick={() => navigate('/dashboard/upload')} 
              className={styles.uploadButtonLarge}
            >
              <Upload size={20} strokeWidth={2} />
              Upload Your First Policy
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.walletContent}>
          {/* ── Summary Strip ── */}
          {completedPolicies.length > 0 && (
            <div className={styles.summaryStrip}>
              {Object.entries(policyGroups).map(([type, policies]) => {
                const TypeIcon = getPolicyTypeIcon(type);
                return (
                  <div key={type} className={`${styles.summaryChip} ${getPolicyTypeColor(type)}`}>
                    <TypeIcon size={16} />
                    <span className={styles.summaryChipCount}>{policies.length}</span>
                    <span className={styles.summaryChipLabel}>
                      {getPolicyTypeDisplay(type).replace(' Insurance', '').replace(' Policy', '')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Policy Cards ── */}
          <div className={styles.policiesGrid}>
            {completedPolicies.map((file) => {
              const policyType = file.metadata_summary?.policy_type || '';
              const TypeIcon = getPolicyTypeIcon(policyType);
              const typeColor = getPolicyTypeColor(policyType);
              const periodLabel = getPolicyPeriodLabel(file.metadata_summary);

              return (
                <button
                  key={file.file_id}
                  className={styles.policyCard}
                  onClick={() => handleViewFile(file.file_id)}
                >
                  {/* Card accent */}
                  <div className={`${styles.cardAccent} ${typeColor}`} />

                  {/* Card content */}
                  <div className={styles.cardBody}>
                    {/* Top row: type tag */}
                    <div className={styles.cardTopRow}>
                      <div className={`${styles.policyTypeTag} ${typeColor}`}>
                        <TypeIcon size={14} />
                        <span>{getPolicyTypeDisplay(policyType)}</span>
                      </div>
                    </div>

                    {/* Insurer name */}
                    <h3 className={styles.insurerName}>
                      {file.metadata_summary?.insurance_company || 'Unknown Insurer'}
                    </h3>

                    {/* Policy details */}
                    <div className={styles.cardDetails}>
                      {file.metadata_summary?.policy_number && (
                        <div className={styles.cardDetailRow}>
                          <Hash size={13} className={styles.cardDetailIcon} />
                          <span className={styles.cardDetailLabel}>Policy</span>
                          <span className={styles.cardDetailValue}>{file.metadata_summary.policy_number}</span>
                        </div>
                      )}

                      {periodLabel && (
                        <div className={styles.cardDetailRow}>
                          <Calendar size={13} className={styles.cardDetailIcon} />
                          <span className={styles.cardDetailLabel}>Period</span>
                          <span className={styles.cardDetailValue}>{periodLabel}</span>
                        </div>
                      )}

                      {!periodLabel && file.metadata_summary?.policy_term_months && (
                        <div className={styles.cardDetailRow}>
                          <Clock size={13} className={styles.cardDetailIcon} />
                          <span className={styles.cardDetailLabel}>Term</span>
                          <span className={styles.cardDetailValue}>{file.metadata_summary.policy_term_months} months</span>
                        </div>
                      )}
                    </div>

                    {/* Bottom row */}
                    <div className={styles.cardBottomRow}>
                      <div className={styles.uploadDate}>
                        <span>Added {formatDate(file.created_at, 'long')}</span>
                      </div>
                      <div className={styles.viewArrow}>
                        <span>View</span>
                        <ChevronRight size={16} />
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Add policy card */}
            <button
              className={styles.addPolicyCard}
              onClick={() => navigate('/dashboard/upload')}
            >
              <div className={styles.addPolicyIcon}>
                <Plus size={28} strokeWidth={1.5} />
              </div>
              <span className={styles.addPolicyLabel}>Add Policy</span>
            </button>
          </div>

          {/* ── Pending/Processing Files ── */}
          {pendingFiles.length > 0 && (
            <div className={styles.pendingSection}>
              <h3 className={styles.pendingSectionTitle}>
                <Clock size={16} />
                Processing
              </h3>
              <div className={styles.pendingList}>
                {pendingFiles.map((file) => {
                  const statusConfig = getFileStatusConfig(file.file_status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div key={file.file_id} className={styles.pendingItem}>
                      <div className={styles.pendingIcon}>
                        <FileText size={20} />
                      </div>
                      <div className={styles.pendingInfo}>
                        <span className={styles.pendingName}>{file.file_name}</span>
                        <span className={styles.pendingDate}>{formatDate(file.created_at, 'long')}</span>
                      </div>
                      <span className={`${styles.pendingBadge} ${statusConfig.className}`}>
                        <StatusIcon 
                          size={12} 
                          className={statusConfig.icon === Loader2 ? styles.spinning : ''}
                        />
                        {statusConfig.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default Policies;